import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { to: "/movement", label: "The Movement" },
  { to: "/journey", label: "Our Journey" },
  { to: "/voices", label: "Voices" },
  { to: "/gather", label: "The Gathering" },
  { to: "/gallery", label: "Gallery" },
  { to: "/launch", label: "Launch" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const darkNav = ["/launch", "/launch/", "/journey", "/journey/"].includes(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          darkNav ? "bg-transparent" : "glass-light"
        } ${scrolled ? `py-3 ${darkNav ? "" : "border-b border-ink/10 card-shadow"}` : "py-5"}`}
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between">
          <Logo />

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.slice(1)}`}
                className={({ isActive }) =>
                  `relative font-display text-xs uppercase tracking-[0.18em] font-semibold transition-colors duration-300 ${
                    isActive ? "text-magenta" : darkNav ? "text-white/80 hover:text-white" : "text-ink/70 hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/conference"
              data-testid="nav-register-cta"
              className="rounded-full brand-gradient px-6 py-2.5 font-display text-xs uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-[0_0_28px_rgba(232,38,111,0.4)]"
            >
              Register
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`md:hidden p-2 ${darkNav ? "text-white" : "text-ink"}`}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-cream md:hidden flex flex-col items-start justify-center gap-6 px-8"
          >
            {LINKS.map((l, i) => (
              <motion.div key={l.to} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                <Link to={l.to} className="font-serif text-4xl text-ink" data-testid={`mobile-nav-${l.to.slice(1)}`}>
                  {l.label}
                </Link>
              </motion.div>
            ))}
            <Link
              to="/conference"
              data-testid="mobile-nav-register"
              className="mt-4 rounded-full brand-gradient px-8 py-3 font-display text-sm uppercase tracking-widest font-bold text-white"
            >
              Register
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
